import { faCircleInfo, faMagnifyingGlass, faTrashAlt } from '@awesome.me/kit-a1e2571845/icons/classic/regular'
import { faCircleXmark } from '@awesome.me/kit-a1e2571845/icons/classic/solid'
import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import MuiIconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { DataGrid, Icon } from '@xweather/theme'
import Fuse from 'fuse.js'
import i18next from 'i18next'
import { enqueueSnackbar } from 'notistack'
import { type ComponentProps, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import type { UserListEntity, UserRoleEntity } from '../../../api/user-manager.ts'
import { NavigateWithMessage } from '../../../components/errors/NotFound.tsx'
import { FormLayout2 } from '../../../components/FormLayout2.tsx'
import { I18n } from '../../../components/I18n.tsx'
import { useConfirmDialog } from '../../../components/useConfirmDialog.tsx'
import { useCreateUserList, useDeleteUserList, userListData, useUpdateUserList } from '../../../data/userLists.ts'
import { currentUserData } from '../../../data/userProfile.ts'
import { type InsightUser, roleData, userData } from '../../../data/users.ts'
import { RoleChip, RoleIcon } from './-roleUtils.tsx'

export const Route = createFileRoute('/_app/users/list/{-$id}')({
	loader: async ({ context, params: { id } }) => {
		const [userLists] = await Promise.all([
			userListData.fetch(context),
			userData.ensure(context),
			currentUserData.ensure(),
			roleData.ensure(),
		])
		const selectedUserList = id ? userLists.find((l) => l.id === id) : undefined
		if (id && !selectedUserList) throw notFound()
		return { selectedUserList }
	},
	notFoundComponent: () => <NavigateWithMessage options={{ to: '/users' }} message={<I18n i18nKey={($) => $.users.userListNotFound} />} />,
	component: UserListForm,
})

function UserListForm() {
	const { selectedUserList } = Route.useLoaderData()
	const navigate = Route.useNavigate()
	const { data: userLists } = userListData.use()
	const { data: users } = userData.use()
	const { data: currentUser } = currentUserData.use()
	const [activeStep, setActiveStep] = useState(0)

	const uiUsers = useMemo(() => users.filter((u) => u.isUiUser), [users])
	const noExistingLists = userLists.length === 0
	const isLimitedAdminEditingDefault = currentUser.role.name === 'LIMITED_ADMIN' && !!selectedUserList?.isDefault
	const canSetDefault = currentUser.role.name !== 'LIMITED_ADMIN' && !selectedUserList?.isDefault
	const canDelete = !!selectedUserList && !selectedUserList.isDefault && !isLimitedAdminEditingDefault

	const form = useForm<UserListFormValues>({
		resolver: zodResolver(createUserListSchema(userLists, selectedUserList?.id)),
		disabled: isLimitedAdminEditingDefault,
		defaultValues: {
			name: selectedUserList?.name ?? '',
			isDefault: selectedUserList?.isDefault ?? noExistingLists,
			userIds: selectedUserList?.userIds ?? [],
		},
	})

	const { mutateAsync: createUserList, isPending: createInProgress } = useCreateUserList()
	const { mutateAsync: updateUserList, isPending: updateInProgress } = useUpdateUserList()
	const { mutateAsync: deleteUserList } = useDeleteUserList()
	const isPending = createInProgress || updateInProgress

	const { Dialog: deleteDialog, openDialog: openDeleteDialog } = useConfirmDialog({
		title: <I18n i18nKey={($) => $.users.list.deleteModalTitle} />,
		content: <I18n i18nKey={($) => $.users.list.deleteModalConfirmation} values={{ userList: selectedUserList?.name }} />,
		actionName: <I18n i18nKey={($) => $.delete} />,
		color: 'error',
		onConfirm: async () => {
			if (!selectedUserList) return
			await deleteUserList(selectedUserList.id, {
				onSuccess: async () => {
					enqueueSnackbar(<I18n i18nKey={($) => $.users.list.userListDeleted} values={{ userList: selectedUserList.name }} />, {
						variant: 'success',
					})
					await navigate({ to: '/users' })
				},
				onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.list.userListDeleteError} />, { variant: 'error' }),
			})
		},
	})

	const onSubmit = form.handleSubmit(
		async (data) => {
			if (selectedUserList) {
				await updateUserList(
					{ id: selectedUserList.id, body: data },
					{
						onSuccess: async () => {
							enqueueSnackbar(<I18n i18nKey={($) => $.users.list.userListUpdated} values={{ userList: data.name }} />, {
								variant: 'success',
							})
							await navigate({ to: '/users', search: { list: selectedUserList.id } })
						},
						onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.list.userListUpdateError} />, { variant: 'error' }),
					},
				)
			} else {
				await createUserList(data, {
					onSuccess: async (created) => {
						enqueueSnackbar(<I18n i18nKey={($) => $.users.list.userListCreated} values={{ userList: data.name }} />, {
							variant: 'success',
						})
						await navigate({ to: '/users', search: { list: created.id } })
					},
					onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.list.userListCreateError} />, { variant: 'error' }),
				})
			}
		},
		() => setActiveStep(0),
	)

	const steps = [
		{
			id: 'listDetails',
			label: <I18n i18nKey={($) => $.users.formSteps.listDetailsLabel} />,
			description: <I18n i18nKey={($) => $.users.formSteps.listDetailsDescription} />,
			form: <DetailsStep users={uiUsers} canSetDefault={canSetDefault} disabled={isLimitedAdminEditingDefault} />,
		},
		{
			id: 'review',
			label: <I18n i18nKey={($) => $.users.formSteps.reviewLabel} />,
			description: <I18n i18nKey={($) => $.users.formSteps.reviewDescription} />,
			form: <ReviewStep users={uiUsers} />,
		},
	]

	return (
		<FormProvider {...form}>
			<Box component='form' sx={{ display: 'contents' }} onSubmit={onSubmit}>
				<FormLayout2
					onBack={() => navigate({ to: '/users' })}
					header={<I18n i18nKey={($) => (selectedUserList ? $.users.list.edit : $.users.list.add)} />}
					sideBar={<FormLayout2.Steps steps={steps} activeStep={activeStep} />}
					bottomBar={
						<>
							{canDelete && (
								<Button color='error' variant='text' startIcon={<Icon icon={faTrashAlt} />} onClick={openDeleteDialog}>
									<I18n i18nKey={($) => $.delete} />
								</Button>
							)}
							<FormLayout2.Buttons
								onStepChange={setActiveStep}
								activeStep={activeStep}
								stepCount={steps.length}
								onCancel={() => navigate({ to: '/users' })}
								onNext={form.trigger}
							/>
							{activeStep === steps.length - 1 && (
								<Button type='submit' disabled={isPending || isLimitedAdminEditingDefault}>
									<I18n i18nKey={($) => $.save} />
									{isPending && <CircularProgress sx={{ ml: 1 }} size={16} />}
								</Button>
							)}
							{deleteDialog}
						</>
					}
				>
					{steps[activeStep]?.form}
				</FormLayout2>
			</Box>
		</FormProvider>
	)
}

type RoleFilter = UserRoleEntity['name'] | 'all'

const DetailsStep = ({ users, canSetDefault, disabled }: { users: InsightUser[]; canSetDefault: boolean; disabled: boolean }) => {
	const { t } = useTranslation()
	const { data: roles } = roleData.use()
	const { control } = useFormContext<UserListFormValues>()
	const [search, setSearch] = useState('')
	const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')

	const roleFilteredUsers = users.filter((u) => roleFilter === 'all' || u.role.name === roleFilter)
	const filteredUsers = search
		? new Fuse(roleFilteredUsers, { keys: ['fullName', ['email', 'email']], threshold: 0.3 }).search(search).map((r) => r.item)
		: roleFilteredUsers

	return (
		<Stack col spacing={2} sx={{ px: 2, pt: 2, h: '100%' }}>
			<Controller
				control={control}
				name='name'
				render={({ field, fieldState }) => (
					<TextField
						{...field}
						required
						sx={{ maxW: 480 }}
						label={<I18n i18nKey={($) => $.users.list.name} />}
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
					/>
				)}
			/>
			<Controller
				control={control}
				name='isDefault'
				render={({ field, fieldState }) => (
					<Stack col>
						<Stack sx={{ align: 'center' }}>
							<FormControlLabel
								label={<I18n i18nKey={($) => $.users.list.setAsDefault} />}
								control={
									<Checkbox
										size='large'
										checked={field.value}
										disabled={!canSetDefault}
										onChange={(e) => field.onChange(e.target.checked)}
									/>
								}
							/>
							<Tooltip
								arrow
								slotProps={{ tooltip: { sx: { w: 320 } } }}
								title={<I18n i18nKey={($) => $.users.list.defaultUserListDescription} />}
							>
								<Icon icon={faCircleInfo} color='textSecondary' />
							</Tooltip>
						</Stack>
						{!!fieldState.error && (
							<Typography variant='body2' color='error'>
								{fieldState.error.message}
							</Typography>
						)}
					</Stack>
				)}
			/>
			<Divider />

			<Stack col spacing={0}>
				<Stack col spacing={0}>
					<Typography variant='h2'>
						<I18n i18nKey={($) => $.users.list.selectUsers} />
					</Typography>
					<Typography variant='body2' color='textSecondary'>
						<I18n i18nKey={($) => $.users.list.selectUsersSubtitle} />
					</Typography>
				</Stack>
				<Stack sx={{ align: 'end' }}>
					<TextField
						placeholder={t(($) => $.searchPlaceholder)}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						sx={{ flexGrow: 1, maxW: 480 }}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position='start'>
										<Icon icon={faMagnifyingGlass} />
									</InputAdornment>
								),
								endAdornment: search && (
									<InputAdornment position='end'>
										<MuiIconButton edge='end' onClick={() => setSearch('')}>
											<Icon icon={faCircleXmark} />
										</MuiIconButton>
									</InputAdornment>
								),
							},
						}}
					/>
					<TextField select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as RoleFilter)} sx={{ minW: 160 }}>
						<MenuItem value='all'>
							<I18n i18nKey={($) => $.users.list.allRoles} />
						</MenuItem>
						{Object.values(roles ?? {}).map(({ name }) => (
							<MenuItem key={name} value={name}>
								<I18n i18nKey={($) => $.users.roles[name]} />
							</MenuItem>
						))}
					</TextField>
				</Stack>
			</Stack>

			<Controller
				control={control}
				name='userIds'
				render={({ field }) => (
					<UserSelectionGrid users={filteredUsers} selectedIds={field.value} onChange={field.onChange} disabled={disabled} />
				)}
			/>
		</Stack>
	)
}

const ReviewStep = ({ users }: { users: InsightUser[] }) => {
	const { watch } = useFormContext<UserListFormValues>()
	const name = watch('name')
	const isDefault = watch('isDefault')
	const userIds = watch('userIds')
	const selectedUsers = useMemo(() => users.filter((u) => userIds.includes(u.id)), [users, userIds])

	return (
		<Stack col spacing={2} sx={{ px: 2, pt: 2, h: '100%' }}>
			<Stack col spacing={0}>
				<Typography variant='h2'>
					<I18n i18nKey={($) => $.users.list.review} />
				</Typography>
				<Typography variant='body2' color='textSecondary'>
					<I18n i18nKey={($) => $.users.list.reviewSubtitle} />
				</Typography>
			</Stack>
			<Divider />
			<Stack sx={{ align: 'baseline' }}>
				<Typography variant='h2'>{name}</Typography>
				{isDefault && <Chip variant='filled' label={<I18n i18nKey={($) => $.users.list.default} />} />}
				<Chip label={<I18n i18nKey={($) => $.users.list.usersCount} values={{ count: selectedUsers.length }} />} />
			</Stack>
			<Box sx={{ flexGrow: 1, minH: 240, overflow: 'hidden' }}>
				<UserGrid users={selectedUsers} />
			</Box>
		</Stack>
	)
}

const UserSelectionGrid = ({
	users,
	selectedIds,
	onChange,
	disabled,
}: {
	users: InsightUser[]
	selectedIds: string[]
	onChange: (ids: string[]) => void
	disabled?: boolean
}) => {
	const selected = new Set(selectedIds)
	const isAllSelected = users.length > 0 && users.every((u) => selected.has(u.id))

	const toggle = (id: string) => onChange(selected.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id])
	const toggleAll = () => {
		const visibleIds = users.map((u) => u.id)
		if (isAllSelected) onChange(selectedIds.filter((id) => !visibleIds.includes(id)))
		else onChange([...new Set([...selectedIds, ...visibleIds])])
	}

	return (
		<UserGrid
			users={users}
			extraCols={[
				{
					id: 'select',
					header: () => (
						<Checkbox
							size='large'
							checked={isAllSelected}
							indeterminate={selected.size > 0 && !isAllSelected}
							disabled={disabled}
							onChange={toggleAll}
						/>
					),
					cell: ({ row }) => (
						<Checkbox
							size='large'
							checked={selected.has(row.original.id)}
							disabled={disabled}
							onChange={() => toggle(row.original.id)}
							onClick={(e) => e.stopPropagation()}
						/>
					),
					size: 56,
				},
			]}
		/>
	)
}

const UserGrid = ({ users, extraCols }: { users: InsightUser[]; extraCols?: ComponentProps<typeof DataGrid<InsightUser>>['columns'] }) => {
	const { t } = useTranslation()
	return (
		<DataGrid
			data={users}
			columns={[
				...(extraCols ?? []),
				{
					accessorKey: 'fullName',
					header: () => <I18n i18nKey={($) => $.users.name} />,
					cell: ({ row }) => (
						<Stack center>
							<RoleIcon role={row.original.role} color='textSecondary' />
							<Typography noWrap>{row.original.fullName}</Typography>
						</Stack>
					),
					meta: { grow: 1 },
				},
				{
					id: 'email',
					accessorFn: (row) => row.email.email,
					header: () => <I18n i18nKey={($) => $.users.email} />,
					cell: ({ row }) => <Typography noWrap>{row.original.email.email}</Typography>,
					meta: { grow: 1 },
				},
				{
					id: 'role',
					accessorFn: (row) => t(($) => $.users.roles[row.role.name]),
					header: () => <I18n i18nKey={($) => $.users.role} />,
					cell: ({ row }) => <RoleChip role={row.original.role} />,
					size: 200,
				},
			]}
			virtualizer={{ estimateSize: () => 56 }}
		/>
	)
}

const createUserListSchema = (userLists: UserListEntity[], currentId: string | undefined) =>
	z
		.object({
			name: z
				.string()
				.trim()
				.min(2, { error: () => i18next.t(($) => $.users.list.listNameTooShortError) })
				.max(80, { error: () => i18next.t(($) => $.users.list.listNameTooLongError) })
				.refine((name) => !userLists.some((l) => l.id !== currentId && l.name.toLowerCase() === name.toLowerCase()), {
					error: () => i18next.t(($) => $.users.list.listNameDuplicateError),
				}),
			isDefault: z.boolean(),
			userIds: z.array(z.string()),
		})
		.refine((data) => !data.isDefault || data.userIds.length > 0, {
			path: ['isDefault'],
			error: () => i18next.t(($) => $.users.list.defaultListEmptyError),
		})
type UserListFormValues = z.infer<ReturnType<typeof createUserListSchema>>
